import { useMemo, useState } from "react";

import { ActionButton, Modal } from "@eisox/design-system";
import { ArrowRightIcon, RoomIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useMutation } from "@tanstack/react-query";

import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

import {
  buildEditableAirHandlerUnitRoomAssociations,
  buildUpdateAirHandlerUnitRoomAssociationsRequestBody,
  isAssociationEdited,
  type EditableAirHandlerUnitRoomAssociation,
} from "../helpers/association.helper";
import { useUpdateAirHandlerUnit } from "../hooks/use-update-air-handler-unit";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";
import { RoomAssociationForm } from "./room-association-form";
import { RoomAssociationList } from "./room-association-list";
import { RoomAssociationPlan } from "./room-association-plan";

const NAME = "RoomAssociationDialog";

interface RoomAssociationDialogProps extends Modal.RootProps {
  buildingId: string;
  children?: React.ReactNode;
}

const RoomAssociationDialog: React.FC<RoomAssociationDialogProps> = ({
  buildingId,
  open: openProp,
  // oxlint-disable-next-line typescript/unbound-method
  onOpenChange,
  defaultOpen,
  children,
}) => {
  const { floors, rooms } = useBuildingContext(NAME);
  const { airHandlerUnits } = useAirHandlerUnitContext(NAME);
  const { mutateAsync } = useUpdateAirHandlerUnit(buildingId);

  const computedAssociations = useMemo(
    () => buildEditableAirHandlerUnitRoomAssociations(airHandlerUnits),
    [airHandlerUnits],
  );

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });

  const [draftAssociations, setDraftAssociations] =
    useState<EditableAirHandlerUnitRoomAssociation[]>(computedAssociations);
  const [selectedAssociationKey, setSelectedAssociationKey] = useState<string | undefined>(
    computedAssociations[0]?.key,
  );

  const selectedAssociation = draftAssociations.find(association => association.key === selectedAssociationKey);
  const isDirty = draftAssociations.some(isAssociationEdited);

  const resetState = () => {
    setDraftAssociations(computedAssociations);
    setSelectedAssociationKey(computedAssociations[0]?.key);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      const body = buildUpdateAirHandlerUnitRoomAssociationsRequestBody(draftAssociations);
      const results = await Promise.allSettled(
        body.flatMap(update =>
          update.airHandlerUnitIds.map(airHandlerUnitId =>
            mutateAsync({ airHandlerUnitId, body: { roomIds: update.roomIds } }),
          ),
        ),
      );
      const firstFailure = results.find(result => result.status === "rejected");
      if (firstFailure?.status === "rejected") throw firstFailure.reason;
    },
    onSuccess: () => {
      setOpen(false);
    },
  });

  const handleOpenChange = (nextOpen: boolean) => {
    resetState();
    setOpen(nextOpen);
  };

  const handleRoomIdsChange = (roomIds: string[]) => {
    if (!selectedAssociation) return;
    setDraftAssociations(previousAssociations =>
      previousAssociations.map(association =>
        association.key === selectedAssociation.key ? { ...association, roomIds } : association,
      ),
    );
  };

  return (
    <Modal.Root open={open} onOpenChange={handleOpenChange}>
      {children && <Modal.Trigger asChild>{children}</Modal.Trigger>}
      <Modal.Content className="flex h-[85vh] w-[85vw] flex-col">
        <Modal.Header
          title={m.air_handler_unit_room_association_dialog_title()}
          subtitle={m.air_handler_unit_room_association_dialog_subtitle()}
          icon={<RoomIcon />}
        >
          <Modal.Close asChild>
            <ActionButton variant="cancel" disabled={isPending}>
              {m.cancel()}
            </ActionButton>
          </Modal.Close>
          <ActionButton disabled={!isDirty || isPending} onClick={() => mutate()}>
            {m.save()}
            <ArrowRightIcon />
          </ActionButton>
        </Modal.Header>
        {selectedAssociation && (
          <div className="gap-10 flex h-[calc(100%-70px-40px)] flex-1">
            <RoomAssociationList
              associations={draftAssociations}
              selectedAssociationKey={selectedAssociation.key}
              onSelect={setSelectedAssociationKey}
            />
            <RoomAssociationForm
              buildingId={buildingId}
              association={selectedAssociation}
              floors={floors}
              rooms={rooms}
              onRoomIdsChange={handleRoomIdsChange}
            />
            <RoomAssociationPlan
              floors={floors}
              rooms={rooms}
              association={selectedAssociation}
              onRoomIdsChange={handleRoomIdsChange}
            />
          </div>
        )}
      </Modal.Content>
    </Modal.Root>
  );
};

RoomAssociationDialog.displayName = NAME;

export { RoomAssociationDialog };
export type { RoomAssociationDialogProps };
